import { useState, useCallback, useEffect } from 'react'

import axios from 'axios'

import { normalizeUseCasesResponse } from 'Pods/LazyLoaded/UseCaseAreaSidebar/Utils/Data'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import isAxiosCancelOrNetworkError from 'Utils/isAxiosCancelOrNetworkError'

const useItemUseCases = (modelId = null) => {
  const pageConfig = usePageConfig()
  const { captureException } = useSentry()

  const [useCases, setUseCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const addUseCaseOptimistic = useCallback((tempUseCase) => {
    setUseCases(prev => [...prev, tempUseCase])
  }, [])

  const removeUseCaseOptimistic = useCallback((tempId) => {
    setUseCases(prev => prev.filter(uc => uc.id !== tempId))
  }, [])

  const updateUseCaseOptimistic = useCallback((useCaseId, updates) => {
    setUseCases(prev => prev.map(uc =>
      uc.id === useCaseId ? { ...uc, ...updates } : uc
    ))
  }, [])

  const fetchUseCases = useCallback((itemId, abortSignal) => {
    const config = {
      withCredentials: true,
      signal: abortSignal,
    }

    return axios.get(`/api/internal/items/${itemId}/item_use_cases`, config)
      .then(({ data }) => {
        setUseCases(normalizeUseCasesResponse(data))
        setLoading(false)
        setError(null)
      })
      .catch((err) => {
        if (!isAxiosCancelOrNetworkError(err)) {
          captureException(err)
          setError(err)
          setLoading(false)
        }
      })
  }, [captureException])

  const refetch = useCallback(() => {
    const itemId = pageConfig?.product?.id || modelId
    if (!itemId) return

    setLoading(true)
    setError(null)
    fetchUseCases(itemId)
  }, [pageConfig?.product?.id, modelId, fetchUseCases])

  useEffect(() => {
    const itemId = pageConfig?.product?.id || modelId
    if (!itemId) return

    let isMounted = true
    setError(null)

    const abortController = new AbortController()

    fetchUseCases(itemId, abortController.signal)
      .then(() => {
        if (!isMounted) {
          setUseCases([])
          setLoading(true)
        }
      })

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [pageConfig?.product?.id, modelId, fetchUseCases])

  return {
    useCases,
    loading,
    error,
    addUseCaseOptimistic,
    removeUseCaseOptimistic,
    updateUseCaseOptimistic,
    refetch,
  }
}

export default useItemUseCases
